import { Button, ButtonGroup, ColorPicker, Divider, Icon, Label, Popover, Select, TextField } from "@shopify/polaris";
import styles from "./CartTemplatesTheme.module.css";
import { ChartHorizontalIcon, ChartVerticalIcon, CheckCircleIcon } from "@shopify/polaris-icons";
import { CartConfigInterface } from "../../CartTypes";
import { Action, ChangeEvents } from "../../../Bundles/Types";
import { Dispatch, SetStateAction, useCallback, useContext, useState } from "react";
import { AvailableCartDrawerTemplates, AvailableCartFooterLayouts, CartWidthOptions } from "../../../Bundles/constants";
import useIsMobile from "../../../../hooks/useIsMobile";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { generateGradientColor, hexToHsla, hslaToHex, lightenHexColor } from "../../../../utilities/parsers";
import { mergeConfig } from "../../../utilities";
import BasicLayoutIcon from "../../../../components/Icons/BasicLayoutIcon";
import ModernLayoutIcon from "../../../../components/Icons/ModernLayoutIcon";
import { CartColors, updateAllFields } from "./CartThemeConstants";
import useSlideCartMixpanelHook from "../../../../hooks/useSlideCartMixpanelHook";

const CartLayoutTemplates = [
    {
        getIcon: ({ primaryColor, secondaryColor, tertiaryColor }: any) => (
            <BasicLayoutIcon
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                tertiaryColor={tertiaryColor}
                size={"100%"}
            />
        ),
        label: "Basic",
        id: AvailableCartDrawerTemplates.BASIC_LAYOUT,
    },
    // { icon: "", label: "Divine", id: AvailableCartDrawerTemplates.DIVINE },
    {
        getIcon: ({ primaryColor, secondaryColor, tertiaryColor }: any) => (
            <ModernLayoutIcon
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                tertiaryColor={tertiaryColor}
                size={"100%"}
            />
        ),
        label: "Modern",
        id: AvailableCartDrawerTemplates.MODERN,
    },
];

const FooterLayoutOptions = [
    {
        getIcon: () => <Icon source={ChartHorizontalIcon} tone="base" />,
        label: "Horizontal",
        id: AvailableCartFooterLayouts.HORIZONTAL,
    },

    {
        getIcon: () => <Icon source={ChartVerticalIcon} tone="base" />,
        label: "Vertical",
        id: AvailableCartFooterLayouts.VERTICAL,
    },
];

const CartTemplatesTheme = ({
    fields,
    cartConfig,
    dispatchCartConfig,
    cart_width,
    showCartPreview,
}: {
    fields: any;
    cartConfig: CartConfigInterface;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    setConfig: Dispatch<SetStateAction<CartConfigInterface>>;
    cart_width: any;
    showCartPreview: boolean;
}) => {
    const { selected_drawer_template, selected_drawer_footer_layout } = fields;
    const { isMobile } = useIsMobile();
    console._log("isMobilee", isMobile);

    const appConfigContext = useContext(AppConfigContext);
    console._log(appConfigContext);

    const [popoverActive, setPopoverActive] = useState<boolean>(false);
    const togglePopoverActive = useCallback(() => setPopoverActive((active) => !active), []);
    const [color, setColor] = useState({
        hex: "#3801ca", // Purple
        hue: 256,
        saturation: 0.9901477832512315,
        brightness: 0.3980392156862745,
        alpha: 1,
    });
    const [hexColor, setHexColor] = useState("");
    const { trackCartFeature } = useSlideCartMixpanelHook();

    const handleColorChange = (newColor: any, overrideTemplate: string = "") => {
        console._log("asdfasfasfasfasdf newColor", newColor);
        const { hue, brightness, saturation } = newColor;
        const hslaColor = `hsla(${hue}, ${saturation * 100}%, ${brightness * 100}%, 1)`;
        const hex = overrideTemplate ? cartConfig?.announcement_design?.message_text_color : hslaToHex(hslaColor);
        const gradientHex = overrideTemplate
            ? generateGradientColor(cartConfig?.announcement_design?.message_text_color, 30)
            : generateGradientColor(hex, 30);
        const lightenHex = overrideTemplate
            ? lightenHexColor(cartConfig?.announcement_design?.message_text_color, 85)
            : lightenHexColor(hex, 85);
        const shouldApplyFully = overrideTemplate?.length
            ? overrideTemplate == AvailableCartDrawerTemplates.BASIC_LAYOUT
            : cartConfig?.selected_drawer_template == AvailableCartDrawerTemplates.BASIC_LAYOUT;

        setHexColor(hex);
        setColor({ ...newColor, hex });

        const updatedConfig: any = {
            ...cartConfig,
            reward_design: {
                ...cartConfig?.reward_design,
                bar_background_color: lightenHex,
                bar_foreground_color: hex,
                ...(shouldApplyFully
                    ? { bar_foreground_gradient_status: false, bar_foreground_gradient_color: "#FFFFFF" }
                    : {
                          bar_foreground_gradient_status: true,
                          bar_foreground_gradient_color: gradientHex,
                      }),
                free_gift_bg_color: lightenHex,
                free_gift_button_bg_color: hex,
                free_gift_button_text_color: brightness >= 0.5 ? "#030303" : "#FFFFFF",
                reward_free_gift_text_color: hex,
                reward_free_gift_product_title_color: hex,
                free_gift_free_label_color: hex,
            },
            announcement_design: {
                ...cartConfig?.announcement_design,
                message_text_color: hex,
                message_bg_color: lightenHex,
                countdown_timer_color: hex,
            },
            upsell_design: {
                ...cartConfig?.upsell_design,
                upsell_button_bg_color: hex,
                upsell_button_bg_hover_color: lightenHex,
                upsell_button_text_color: brightness >= 0.5 ? "#303030" : "#FFFFFF",
                upsell_button_border_color: hex,
                upsell_button_hover_border_color: hex,
                swatches_select_color: hex,
                upsell_button_text_hover_color: hex,
                upsell_product_title_size: 14,
                upsell_product_title_style: "650 normal",
                ...(shouldApplyFully
                    ? {
                          upsell_bg_color: "#FFFFFF",
                          upsell_title_color: hex,
                          upsell_product_bg_color: lightenHex,
                      }
                    : {
                          upsell_bg_color: "#F3F3F3",
                          upsell_title_color: "#303030",
                          upsell_product_bg_color: "#FFFFFF",
                      }),
            },
            discount: {
                ...cartConfig?.discount,
                ...(shouldApplyFully
                    ? {
                          title: "<p><strong>Discount Coupon</strong></p>",
                      }
                    : {
                          title: "<p>Discount Coupon</p>",
                      }),
            },
            discount_design: {
                ...cartConfig?.discount_design,
                cta_bg_hover_color: "#f1f1f1",
                cta_text_color: "#f1f1f1",
                cta_bg_color: hex,
                cta_text_hover_color: hex,
                title_color: hex,
                code_bg_color: lightenHex,
                code_text_color: hex,
            },
            additional_note_design: {
                ...cartConfig?.additional_note_design,
                cta_bg_hover_color: "#f1f1f1",
                cta_text_color: "#f1f1f1",
                cta_bg_color: hex,
                cta_text_hover_color: hex,
                title_color: hex,
            },
            additional_product_design: {
                ...cartConfig?.additional_product_design,
                additional_product_title_color: "#303030",
                additional_product_description_color: "#8a8a8a",
                additional_product_card_bg: "#FFFFFF",
                ...(shouldApplyFully
                    ? {
                          additional_product_border_gradient_status: false,
                          additional_product_title_size: 12,
                          additional_product_description_size: 10,
                          addon_modal_title_size: 14,
                      }
                    : {
                          additional_product_title_size: 14,
                          additional_product_description_size: 11,
                          additional_product_border_color: hex,
                          additional_product_border_gradient_status: true,
                          additional_product_border_gradient_color: gradientHex,
                      }),
                addon_modal_background_color: lightenHex,
                addon_modal_title_color: hex,

                addon_modal_primary_button_bg: hex,
                addon_modal_primary_button_text_color: brightness >= 0.5 ? "#303030" : "#e4e4e4ff",

                addon_modal_secondary_button_text_color: hex,
            },
            additional_message: {
                ...cartConfig?.additional_message,
                additional_msg_bg: "#FFFFFF",
                additional_msg_color: "#303030",
            },
            cart_design: {
                ...cartConfig?.cart_design,
                cart_primary_color: hex,
                cart_lighten_primary_color: lightenHex,
                cart_gradient_color: gradientHex || lightenHex,
                cart_bg_color: "#ffffff",
                cartline_card_title_size: 14,
                cartline_card_title_style: "650 normal",
                cartline_card_qty_color: "#303030",
                footer_discount_tag_bg_color: hex,
                footer_discount_tag_text_color: brightness > 0.5 ? "#303030" : "#FFFFFF",
                footer_button_bg_color: hex,
                footer_custom_shipping_button_color: hex,
                footer_button_border_color: hex,
                footer_button_hover_border_color: hex,
                footer_button_bg_hover_color: lightenHex,
                footer_button_text_color: brightness >= 0.5 ? "#303030" : "#FFFFFF",
                footer_button_text_hover_color: hex,
                cartline_card_discount_bg_color: hex,
                cartline_card_discount_text_color: brightness > 0.5 ? "#303030" : "#FFFFFF",
                footer_bg_color: "#FFFFFF",

                // Header
                ...(shouldApplyFully
                    ? {
                          header_bg_color: hex,
                          header_title_color: brightness > 0.5 ? "#303030" : "#FFFFFF",
                          header_quantity_color: brightness > 0.5 ? "#303030" : "#FFFFFF",
                          close_button_color: brightness > 0.5 ? "#303030" : "#FFFFFF",
                          header_title_underline_color: hex,
                          header_title_underline_color_gradient_status: false,
                          header_title_underline_color_gradient_color: "#FFFFFF",
                          cartline_card_bg_color: "#ffffff",
                          // Footer
                      }
                    : {
                          header_bg_color: "#FFFFFF",
                          header_title_color: "#303030",
                          header_quantity_color: "#303030",
                          close_button_color: "#303030",
                          header_title_underline_color: hex,
                          header_title_underline_color_gradient_status: true,
                          header_title_underline_color_gradient_color: gradientHex,
                          cartline_card_bg_color: "#FFFFFF",
                          // Footer
                      }),
            },
        };
        const _c = mergeConfig(cartConfig, updatedConfig);
        if (overrideTemplate?.length) {
            _c.selected_drawer_template = overrideTemplate as any;
        }
        updateAllFields(fields, updatedConfig);

        dispatchCartConfig({
            type: ChangeEvents.initState,
            init: {
                ..._c,
            },
        });
    };

    return (
        <>
            <div className={styles.cartLayoutsWrapper}>
                <Label id="template_layout">Template Layouts</Label>
                <div className={styles.layouts}>
                    {CartLayoutTemplates.map((clt) => {
                        return (
                            <div
                                className={`${styles.cartLayout} ${
                                    cartConfig?.selected_drawer_template == clt.id && styles.selectedLayout
                                }`}
                                onClick={() => {
                                    selected_drawer_template?.onChange(clt.id);
                                    handleColorChange(color, clt.id as any);
                                }}
                            >
                                <div className={styles.layoutLabel}>
                                    {clt?.label}{" "}
                                    <span className={styles.currentLayoutIcon}>
                                        <Icon source={CheckCircleIcon} tone={"success"} />
                                    </span>
                                </div>
                                <div className={styles.layoutImg}>
                                    {clt?.getIcon({
                                        primaryColor: "#303030",
                                        secondaryColor: "#E0E0E0",
                                        tertiaryColor: "#F7F7F7",
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className={styles.cartLayoutsWrapper}>
                <Label id="solid_color">Footer Layouts</Label>
                <div className={styles.footerLayouts}>
                    {FooterLayoutOptions.map((cfl) => {
                        return (
                            <div
                                className={`${styles.footerLayout} ${
                                    cartConfig?.selected_drawer_footer_layout == cfl.id && styles.selectedLayout
                                }`}
                                onClick={() => {
                                    selected_drawer_footer_layout?.onChange(cfl.id);
                                    dispatchCartConfig({
                                        payload: cfl.id,
                                        key: "selected_drawer_footer_layout",
                                    });
                                }}
                            >
                                <div className={styles.layoutLabel}>
                                    {cfl?.label}{" "}
                                    <span className={styles.currentLayoutIcon}>
                                        <Icon source={CheckCircleIcon} tone={"success"} />
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={`${styles.formRow} ${!showCartPreview && styles.row3}`}>
                <Select
                    label={"Cart Width"}
                    options={CartWidthOptions}
                    onChange={(opt) => {
                        dispatchCartConfig({
                            payload: {
                                cart_width: opt,
                            },
                            isComplex: true,
                            key: "cart_design",
                        });
                        cart_width.onChange(opt);
                        trackCartFeature({ cta: opt, ctaType: "Cart Width", featName: "Cart Design" });
                    }}
                    value={cart_width.value}
                />
            </div>
            <Divider />

            <div className={styles.cardSubLabel} style={{ marginBottom: -5 }}>
                Appearance
            </div>
            <div className={styles.colorWrapper}>
                <Label id="solid_color">Theme Colors</Label>
                <div
                    style={{
                        display: "flex",
                        gap: "10px",
                        position: "relative",
                    }}
                >
                    <ButtonGroup variant="segmented" noWrap>
                        {CartColors.map((t: any) => (
                            <Button
                                size="micro"
                                onClick={() => {
                                    handleColorChange(t);
                                }}
                            >
                                {/* @ts-expect-error no react elements allowed */}
                                <div
                                    style={{
                                        backgroundColor: t?.hex,
                                    }}
                                    className={styles.colorBtn}
                                ></div>
                            </Button>
                        ))}

                        <Popover
                            active={popoverActive}
                            preferredAlignment="right"
                            activator={
                                <Button
                                    size="micro"
                                    onClick={() => {
                                        togglePopoverActive();
                                    }}
                                >
                                    {/* @ts-expect-error no react elements allowed */}
                                    <div
                                        style={{
                                            height: "20px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            fontWeight: "500",
                                            position: "relative",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                width: "20px",
                                                height: "20px",
                                                border: "2px dotted #d2d5db",
                                                borderRadius: "50%",
                                                fontSize: "16px",
                                            }}
                                        >
                                            +
                                        </span>
                                        <span className={styles.customText}>Custom</span>
                                    </div>
                                </Button>
                            }
                            onClose={togglePopoverActive}
                        >
                            <div
                                style={{
                                    // position: "absolute",
                                    // top: "30px",
                                    // right: "0",
                                    backgroundColor: "#ffffff",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    zIndex: 10,
                                    border: "1px solid lightgray",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                }}
                            >
                                <ColorPicker onChange={handleColorChange} color={color} />
                                <TextField
                                    label=""
                                    autoComplete="off"
                                    placeholder={"#ffffff"}
                                    maxLength={7}
                                    value={hexColor}
                                    onChange={(val) => {
                                        console._log(val);
                                        setHexColor(val);
                                        if (val.length == 7 && val.startsWith("#")) {
                                            const hslaColor = hexToHsla(val);
                                            handleColorChange(hslaColor);
                                        }
                                    }}
                                />
                            </div>
                        </Popover>
                    </ButtonGroup>
                </div>
            </div>
        </>
    );
};

export default CartTemplatesTheme;
